import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { srBodyTemplate, CommonBodyForTable, commonFormDialogFooter } from "../Common/CommonComponents";
import ApiServices from "../../service/api/ApiServices";
import { Dialog } from "primereact/dialog";
import { CommonForDetails } from "../CRM/Leads";
import { Toast } from "primereact/toast";
import { failureToast, successToast } from "../Common/CommonFunctions";
import { OrderDetails } from "./OrderDetails";
import { Toolbar } from "primereact/toolbar";
import { TabPanel, TabView } from "primereact/tabview";
import { Timeline } from "primereact/timeline";
import { Card } from "primereact/card";
import classNames from 'classnames';
import { CommisionDetails } from "./CommisionDetails";

const saved = localStorage.getItem("user");

// Parse the saved user data from localStorage.
// This is typically used to get user information or authentication details.
const initialValue = JSON.parse(saved);

// Create an instance of ApiServices to use its methods for API calls.
// Assuming ApiServices is a class th   at handles API requests.
const apiServices = new ApiServices()

/** * AllOrders component to display a list of AllOrders in a data table.
 * It fetches the AllOrders data from the API and displays it in a paginated table format.
 * It also includes a search functionality to filter the AllOrders based on their names or other attributes.
 */
export const AllOrders = () => {

    const [ordersList, setAllOrdersList] = useState(null)
    const [loading, setLoading] = useState(false)
    const [globalFilter, setGlobalFilter] = useState('');
    const [viewSalesMemberDialog, setViewSalesMemberDialog] = useState(false);
    const [order, setOrder] = useState({
        personal_first_name: '',
        personal_last_name: '',
        user_id: '',
        order_no: ''
    });
    const [commissionDetails, setCommissionDetails] = useState([]);

    const [orderTimeStamp, setOrderTimeStamp] = useState(null)

    // Create a ref for the toast component to show notifications.
    const toast = useRef(null);
    const dataRef = useRef(null);


    useEffect(() => {
        getAllOrders()
    }, [])

    const tableStyle = { overflowX: 'scroll', width: '100%' };

    const exportCSV = () => {
        dataRef.current.exportCSV();
    }

    const header = (
        <div className="table-header">
            <span className="p-input-icon-right">
                <h6 className="p-mt-3 p-mr-5">Orders</h6>
            </span>
            <span className="p-input-icon-left">
                <div className="p-inputgroup">
                    <Button className='custom-group-btn' icon="pi pi-search" />
                    <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
                    <Button className='custom-group-btn p-button-rounded p-m-2' onClick={() => { exportCSV(dataRef) }} icon="pi pi-download"></Button>
                </div>
            </span>
        </div>
    )

    /**
     * Function to fetch AllOrders data from the API
     * and update the state with the fetched data.
     */
    const getAllOrders = async (data = {}) => {
        setLoading(true)
        const response = await apiServices.getGSFAllOrders(data)   // Assuming this API returns the AllOrders data
        if (response?.data?.response_obj.length > 0) {
            const modifiedOrders = response.data.response_obj.map(order => ({
                ...order,
                razorpay_amount: order.razorpay_amount ? order.razorpay_amount / 100 : 0
            }));
            setAllOrdersList(modifiedOrders)
        } else {
            setAllOrdersList([])
        }
        setLoading(false)
    }

    const openOrderDetails = (rowData) => {
        setOrder({
            user_id: rowData.customer_id,
            order_no: rowData.order_no,
            first_name: rowData.personal_first_name,
            last_name: rowData.personal_last_name,
        });
        setCommissionDetails(rowData.commision_distribution || []);
        setViewSalesMemberDialog(true);
        getTrackingDetails(rowData)
    }

    /** * Function to render the action buttons for each row in the data table.
     * It includes Edit and View buttons with appropriate styles and actions.
     * @param {Object} rowData - The data of the current row.
     * @returns JSX element containing action buttons.
     */
    const actionBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Actions</span>
                <div className="actions" style={{ display: "flex" }}>
                    <Button icon="pi pi-eye" title='View Details' className="p-button-rounded p-button-info p-mx-2" aria-label="View Details" onClick={() => { openOrderDetails(rowData) }} />
                </div>
            </>
        );
    }

    /**
     * Function to handle the visibility of the view sales member dialog.
     * It sets the state to show or hide the dialog based on the parameter passed.
     * @param {boolean} value - The value to set the visibility of the dialog.
     */
    const hideviewDialog = () => {
        setViewSalesMemberDialog(false);
    }


    /**
     * Function to render the footer of the view sales member dialog.
     * It includes Cancel and View buttons with appropriate actions.
     */
    const viewSalesFooter = commonFormDialogFooter('Close', hideviewDialog, 'Okay', 'view-SalesMember-form')


    const rightToolbarTemplate = () => {
        return (
            <>
                {/* <Button label="Place Order" icon="pi pi-user-edit" className="p-custom-btn p-ml-2" onClick={() => () => { }} /> */}
            </>
        )
    }

    const openPI = (data) => { data && window.open(data, '_blank') }
    const customizedContent = (item) => (<Card title={item.title} subTitle={item.date}><p>{item.description}</p> </Card>)
    const customizedMarker = (item) => (<span className="custom-marker p-shadow-2" style={{ backgroundColor: item.color }}><i className={classNames('marker-icon', item.icon)}></i></span>)

    const getTrackingDetails = (data) => {
        if (data) {
            console.log(data);
            const timestamp = data.timestamps;
            const arr = [];
            for (const key in timestamp) {

                if (key == 'received') {
                    const ob = {
                        index: 1,
                        status: [key],
                        date: timestamp[key],
                        description: 'Your order placed successfully.',
                        title: 'Order Placed',
                        icon: timestamp[key] ? 'pi pi-check' : 'pi pi-times',
                        color: timestamp[key] ? '#037049' : '#c61414'
                    }
                    arr.push(ob)
                }

                if (key == 'received') {
                    const ob = {
                        index: 2,
                        status: [key],
                        date: timestamp[key],
                        description: 'Your order has been accepted.',
                        title: 'Order Accepted',
                        icon: timestamp[key] ? 'pi pi-check' : 'pi pi-times',
                        color: timestamp[key] ? '#037049' : '#c61414'
                    };
                    arr.push(ob)
                }

                if (key == 'payment_confimed') {
                    const ob = {
                        index: 3,
                        status: [key],
                        date: timestamp[key],
                        description: 'Your order payment was updated successfully.',
                        title: 'Order Payment',
                        icon: timestamp[key] ? 'pi pi-check' : 'pi pi-times',
                        color: timestamp[key] ? '#037049' : '#c61414'
                    };

                    arr.push(ob)
                }

                if (key == 'dispatched') {
                    const ob = {
                        index: 4,
                        status: [key],
                        date: timestamp[key],
                        description: 'Your order has been dispatched.',
                        title: 'Order Dispatched',
                        icon: timestamp[key] ? 'pi pi-check' : 'pi pi-times',
                        color: timestamp[key] ? '#037049' : '#c61414'
                    };
                    arr.push(ob)
                }

                if (key == 'delivered') {

                    const ob = {
                        index: 5,
                        status: [key],
                        date: timestamp[key],
                        description: `Your order has been delivered.`,
                        title: `Order Delivered`,
                        icon: timestamp[key] ? 'pi pi-check' : 'pi pi-times',
                        color: timestamp[key] ? '#037049' : '#c61414'
                    };

                    arr.push(ob)
                }


            }

            const sortedArr = arr.sort((a, b) => a.index - b.index);
            setOrderTimeStamp(sortedArr);
        }
    }
    const CommonBodyForTable1 = (data, props) => (<> <span className="p-column-title">{props.header}</span>{data[props.field] ? data[props.field] : '-'} </>)



    return (

        <>
            <div className="p-grid crud-demo">
                <div className="p-col-12">
                    <div className="card">
                        <Toast ref={toast} position="center" />
                        {
                            loading ?
                                <>
                                    <div className="spinner-div">
                                        <center><ProgressSpinner strokeWidth="7" animationDuration='4s' /></center>
                                    </div>
                                </>
                                :
                                <>
                                    {/* <Toolbar className="p-mb-4 p-toolbar" right={rightToolbarTemplate}></Toolbar> */}
                                    <DataTable style={tableStyle} className="datatable-responsive" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                                        value={ordersList} dataKey="id" exportFilename={`GSF Orders Report`}
                                        loading={loading} globalFilter={globalFilter} filterDisplay="menu" header={header} ref={dataRef}>
                                        <Column field="srid" header="Sr.No." body={srBodyTemplate} style={{ width: '1rem' }}></Column>
                                        {/* <Column field="personal_first_name" header="First Name" body={CommonBodyForTable} style={{ width: '2rem' }} exportable={true}></Column>
                                        <Column field="personal_last_name" header="Last Name" body={CommonBodyForTable} style={{ width: '2rem' }} exportable={true}></Column> */}
                                        <Column field="Full Name" header="Customer Name" body={(rowData) => `${rowData.personal_first_name || ''} ${rowData.personal_last_name || ''}`.trim()} style={{ width: '4rem' }} exportable={true}></Column>
                                        <Column field="phone_number" header="Phone Number" body={CommonBodyForTable} style={{ width: '3rem' }} exportable={true}></Column>
                                        <Column field="order_no" header="Order Number" sortable body={CommonBodyForTable} style={{ width: '3rem' }} exportable={true}></Column>
                                        <Column field="payment_status" header="Payment Status" sortable body={CommonBodyForTable} style={{ width: '3rem' }} exportable={true}></Column>
                                        <Column field="status" header="Status" sortable body={CommonBodyForTable} style={{ width: '3rem' }} exportable={true}></Column>
                                        <Column field="razorpay_amount" header="Razorpay Amount" sortable body={CommonBodyForTable} style={{ width: '4rem' }} exportable={true}></Column>
                                        <Column field="createdAt" header="Order Date" sortable body={CommonBodyForTable} style={{ width: '3rem' }} exportable={true}></Column>
                                        <Column field="action" header="Actions" body={actionBodyTemplate} style={{ width: '2rem' }}></Column>
                                    </DataTable>
                                </>
                        }

                        <Dialog visible={viewSalesMemberDialog} style={{ width: '100%' }} header={`${order?.first_name} ${order?.last_name} - ${order.order_no}`} modal footer={viewSalesFooter} onHide={hideviewDialog}>
                            <TabView>
                                <TabPanel header="Order Details">
                                    <OrderDetails order={order} />
                                </TabPanel>
                                <TabPanel header="Commission Details">
                                    <CommisionDetails commissionDetails={commissionDetails} />
                                </TabPanel>
                                <TabPanel header="Track Order">

                                    <div className="p-grid timeline-demo">
                                        <div className="p-col-3"> </div>
                                        <div className="p-col-6">
                                            <div className="card">
                                                <Timeline value={orderTimeStamp} align="alternate" className="customized-timeline" marker={customizedMarker} content={customizedContent} />
                                            </div>
                                        </div>
                                        <div className="p-col-3"> </div>
                                    </div>
                                </TabPanel>
                            </TabView>
                        </Dialog>

                    </div>
                </div>
            </div>
        </>
    )
}